import React, { useState } from 'react';
import './QuickInspection.css';

interface Section {
  id?: number;
  numero: number;
  libelle: string;
  longueur: number;
  largeur: number;
}

interface QuickInspectionProps {
  sections: Section[];
  onUpdate: (sectionId: number, field: keyof Section, value: any) => Promise<void>;
  onClose: () => void;
}

const QuickInspection: React.FC<QuickInspectionProps> = ({ sections, onUpdate, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [values, setValues] = useState<Record<string, any>>({});

  const currentSection = sections[currentIndex];
  const progress = ((currentIndex + 1) / sections.length) * 100;

  const handleNext = () => {
    // Save current values
    if (currentSection.id) {
      Object.entries(values).forEach(([field, value]) => {
        onUpdate(currentSection.id!, field as keyof Section, value);
      });
    }
    
    if (currentIndex < sections.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setValues({});
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setValues({});
    }
  };

  const handleQuickRate = (field: string, value: number) => {
    setValues({ ...values, [field]: value });
  };

  return (
    <div className="quick-inspection-overlay">
      <div className="quick-inspection-modal">
        <div className="inspection-header">
          <h2>🚀 Inspection Rapide</h2>
          <button className="close-inspection" onClick={onClose}>✕</button>
        </div>

        <div className="inspection-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="progress-text">
            Tronçon {currentIndex + 1} / {sections.length}
          </div>
        </div>

        <div className="inspection-content">
          <div className="section-info">
            <h3>📏 {currentSection.libelle}</h3>
            <div className="section-details">
              <span>Longueur: {currentSection.longueur}m</span>
              <span>Largeur: {currentSection.largeur}m</span>
              <span>Surface: {(currentSection.longueur * currentSection.largeur).toFixed(0)}m²</span>
            </div>
          </div>

          <div className="quick-ratings">
            {[
              { field: 'surface_glissante', label: '🧊 Surface glissante', icon: '🧊' },
              { field: 'degradation_revetement', label: '🛣️ Dégradation', icon: '🛣️' },
              { field: 'fissures_longitudinales', label: '↕️ Fissures long.', icon: '↕️' },
              { field: 'fissures_transversales', label: '↔️ Fissures trans.', icon: '↔️' },
              { field: 'nids_poule', label: '🕳️ Nids de poule', icon: '🕳️' },
              { field: 'deformation', label: '〰️ Déformation', icon: '〰️' }
            ].map(({ field, label, icon }) => (
              <div key={field} className="rating-row">
                <span className="rating-label">{label}</span>
                <div className="rating-buttons">
                  {[0, 1, 2, 3, 4, 5].map(value => (
                    <button
                      key={value}
                      className={`rating-btn ${values[field] === value ? 'active' : ''} level-${value}`}
                      onClick={() => handleQuickRate(field, value)}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="inspection-footer">
          <button 
            className="btn-previous" 
            onClick={handlePrevious}
            disabled={currentIndex === 0}
          >
            ← Précédent
          </button>
          <button className="btn-skip" onClick={handleNext}>
            Passer
          </button>
          <button className="btn-next" onClick={handleNext}>
            {currentIndex === sections.length - 1 ? '✓ Terminer' : 'Suivant →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickInspection;
